// Copyright (c) Docugami, Inc. All rights reserved.

import { Dictionary } from "@reduxjs/toolkit";
import { createFolderId } from "components/file_management/file_helpers";
import { ChunkInstance, TokenRange } from "networking/api";
import { AppDispatch, AppState } from "store";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { dgmlSlice, selectChunkBySpanId } from "store/dgml/dgmlSlice";
import { Chunk, ChunkStructure, SimpleColumn, isChunkAddedChunk } from "store/dgml/dgmlTypes";
import { SpanId, SpanIdUtils } from "utils/SpanIdUtils";
import { isValidDecodedTag } from "utils/TagStandardUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { Token, TokensTextParser } from "utils/TokensTextParser";
import { DgmlInstancesSingleton } from "../../hooks/documentDgml";

const Ltxml = require('../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const XElement = Ltxml.XElement;
const XAttribute = Ltxml.XAttribute;
const XText = Ltxml.XText;
const XName = Ltxml.XName;

export const isSimpleTable = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): boolean => {
    const rows = dispatch(getTableRows(folderId, spanId));

    if (!rows.length) {
        return false;
    }

    const fid = createFolderId(folderId);
    const chunks = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data?.chunks;
    if (!chunks) {
        return false;
    }

    const selectChunk = (spanId: string): Chunk => selectChunkBySpanId(chunks, spanId)!;
    return isSimpleChunks(rows[0].children.map(x => selectChunk(x)));
}

const isSimpleChunks = (chunks: Chunk[]): boolean => chunks.findIndex(x => x.structure?.colspan !== undefined && x.structure.colspan > 1) === -1;

export const getTableRows = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): Chunk[] => {
    const fid = createFolderId(folderId);

    const chunks = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data?.chunks;
    if (!chunks) {
        return [];
    }

    const chunk = chunks.entities[spanId];
    if (chunk?.structure?.type !== ChunkStructure.Table) {
        return [];
    }

    const selectChunk = (spanId: string): Chunk => selectChunkBySpanId(chunks, spanId)!;

    const tbody = chunk.children.map(selectChunk).find(x => x.structure?.type === ChunkStructure.TableBody);
    const rows = tbody?.children.map(selectChunk).filter(x => x.structure?.type === ChunkStructure.TableRow) ?? [];
    return rows;
}

export const isSimpleColumnHeader = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): boolean => {
    const fid = createFolderId(folderId);

    const chunks = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data?.chunks;
    if (!chunks) {
        return false;
    }

    const chunk = chunks.entities[spanId];
    if (chunk?.structure?.type !== ChunkStructure.TableCell) {
        return false;
    }

    const selectChunk = (spanId: string): Chunk => selectChunkBySpanId(chunks, spanId)!;

    const row = selectChunk(chunk.parent!);
    const rowIndex = selectChunk(row.parent!).children.indexOf(row.spanId);
    if (rowIndex !== 0) {
        return false;
    }

    return isSimpleChunks(row.children.map(x => selectChunk(x)));
}

export const getParentTable = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): Chunk | undefined => {
    const fid = createFolderId(folderId);

    const chunks = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data?.chunks;
    if (!chunks) {
        return;
    }

    const selectChunk = (spanId: string): Chunk => selectChunkBySpanId(chunks, spanId)!;
    let chunk: Chunk | undefined = selectChunk(spanId);
    while (!!chunk && chunk.structure?.type !== ChunkStructure.Table) {
        chunk = !!chunk.parent ? selectChunk(chunk.parent) : undefined;
    }

    return chunk?.structure?.type === ChunkStructure.Table ? chunk : undefined;
}

export const getSimpleColumns = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): SimpleColumn[] => {
    const fid = createFolderId(folderId);

    const chunks = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data?.chunks;
    if (!chunks) {
        return [];
    }

    const isSimple = dispatch(isSimpleTable(folderId, spanId));
    if (!isSimple) {
        return []
    }

    const rows = dispatch(getTableRows(folderId, spanId));
    if (!rows.length) {
        return []
    }

    const selectChunk = (spanId: string): Chunk => selectChunkBySpanId(chunks, spanId)!;
    const header = rows[0];
    const remainingRows = rows.slice(1).filter(row => row.children.length === header.children.length && isSimpleChunks(row.children.map(child => selectChunk(child))));

    return header.children.map<SimpleColumn>((cellSpanId, index) => ({
        header: selectChunk(cellSpanId),
        rows: remainingRows.map(row => selectChunk(row.children[index]))
    }));
}

export const addLabel = async (docsetId: string, spanId: string, label: string): Promise<ChunkInstance> => {
    label = label.trim();
    if (!isValidDecodedTag(label)) {
        throw new Error("Cannot label chunk w/ invalid label");
    }

    const spanIdInfo = SpanIdUtils.parse(spanId);
    const folderId = `${docsetId}:${spanIdInfo.fileId}`;
    let chunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId);
    if (!chunk) {
        throw new Error("Could not find chunk for target spanId");
    }

    if ((chunk.chunk?.labels.findIndex(x => x.toolId === "cp" && x.value === label) ?? -1) !== -1) {
        // Label already exists on the chunk
        return chunk;
    }

    chunk = await DgmlInstancesSingleton.label({
        docsetId: docsetId,
        fileId: spanIdInfo.fileId,
        spanId: spanId,
        label: label,
        dgmlVersion: DgmlInstancesSingleton.getDgmlVersion(folderId)!.value
    });

    return chunk;
}

export const getSpanIdForTokenRange = (folderId: string, tokenRange: TokenRange) => (dispatch: AppDispatch, getState: () => AppState): SpanId | undefined => {
    const fid = createFolderId(folderId);
    const mappedTokens = selectMappedTokens(getState(), fid.clusterId, fid.fileId);
    if (!mappedTokens) {
        return;
    }

    const keyRange = mappedTokens.tokenRangeToTokenKeyRange(tokenRange);
    return SpanIdUtils.create(fid.fileId, keyRange.fromTokenKey, keyRange.toTokenKey);
}

interface NodeState {
    chunk: Chunk;
    node: any;
}

export const createXhtml = (root: Chunk, chunks: Dictionary<Chunk>, tokens?: Token[]): any => {
    // Handle added chunks
    if (isChunkAddedChunk(root)) {
        const rootElement = new XElement(new XName('span'));
        rootElement.nodesArray.push(new XText(root.addedChunkValue ?? ''));
        return rootElement;
    }

    const stack: Chunk[] = [root];
    const openStack: NodeState[] = [];
    let rootState: NodeState | undefined = undefined;
    const tokensTextParser = !!tokens ? new TokensTextParser(tokens) : undefined;
    let nextWriteToken = root.tokenRange.fromTok;

    const writeText = (toTok: number, nodeState: NodeState) => {
        if (!tokensTextParser) {
            return;
        }

        const text = tokensTextParser.getTextForTokenRange({ fromTok: nextWriteToken, toTok: toTok }, true).join(" ");
        nodeState.node.nodesArray.push(new XText(text));
        nextWriteToken = toTok;
    }

    while (stack.length > 0) {
        const current = stack.pop()!;
        while (openStack.length > 0 && !!current.parent && openStack[openStack.length - 1].chunk.spanId !== current.parent) {
            const openNode = openStack[openStack.length - 1];
            if (!!tokensTextParser && openNode.chunk.tokenRange.toTok > nextWriteToken) {
                writeText(openNode.chunk.tokenRange.toTok, openNode);
            }

            openStack.pop();
        }

        const parent = openStack[openStack.length - 1];
        if (!!rootState) {
            // Skip nodes w/o structure or those that are not contained in the root range
            const skip = !current.structure || !TokenRangeUtils.rangeIsContained(current.tokenRange, root.tokenRange);
            if (skip) {
                for (let i = current.children.length - 1; i >= 0; --i) {
                    stack.push({ ...chunks[current.children[i]]!, parent: parent.chunk.spanId });
                }

                continue;
            }
        }

        const currentState: NodeState = {
            chunk: current,
            node: createNode(current, chunks)
        };

        if (!currentState.node) {
            nextWriteToken = current.tokenRange.toTok;
            continue;
        }

        if (!!tokensTextParser && current.tokenRange.fromTok > nextWriteToken) {
            writeText(currentState.chunk.tokenRange.fromTok, currentState);
        }

        if (!!parent) {
            parent.node.nodesArray.push(currentState.node);
            currentState.node.parent = parent.node;
        }

        openStack.push(currentState);

        for (let i = current.children.length - 1; i >= 0; --i) {
            stack.push(chunks[current.children[i]]!);
        }

        if (!rootState) {
            rootState = currentState;
        }
    }

    while (!!tokensTextParser && openStack.length > 0) {
        const state = openStack.pop()!;
        if (state.chunk.tokenRange.toTok > nextWriteToken) {
            writeText(state.chunk.tokenRange.toTok, state);
        }
    }

    return rootState?.node;
}

const createNode = (chunk: Chunk, chunks: Dictionary<Chunk>): any => {
    const elementName = getElementName(chunk);
    const xhtmlElement = new XElement(new XName(elementName));
    if (!!chunk.structure) {
        const structureParts = chunk.structure.value.split("|")[0].split(" ");

        let i = 1;
        while (i < structureParts.length - 1) {
            const attr = new XAttribute(structureParts[i], structureParts[i + 1]);
            xhtmlElement.add(attr);
            i += 2;
        }

        if (!!chunk.style) {
            xhtmlElement.add(new XAttribute("style", chunk.style.value));
        }

        if (chunk.structure.type === ChunkStructure.ListItemMarker) {
            const parentList = chunks[chunks[chunk.parent!]?.parent ?? ''];
            if (!!parentList?.style?.value.includes("list-style-type:") && !parentList.style.value.includes("list-style-type: none")) {
                // Drop this node since the token that the list item marker represents will be handled by the list styling
                return;
            }
        }
    }

    return xhtmlElement;
}

const getElementName = (chunk: Chunk): string => {
    let name = chunk.structure?.type ?? 'div';
    if (name === ChunkStructure.ListItemMarker) {
        name = ChunkStructure.Span;
    }

    return name;
}
